import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Button,
  TextInput,
  AppDialog,
  ConfirmActionModal,
} from '../Common'
import { useNls } from '../../nls/nlsProvider'
import { useAuth } from '../../context/useAuth'
import type {
  SharedDevicePersonSummary,
} from '../../types/ops'
import {
  validateDevicePin,
} from '../../types/ops'
import {
  getStoredSharedDeviceCredentials,
  setStoredSharedDeviceCredentials,
  clearStoredSharedDeviceCredentials,
  enrolSharedDevice,
  listSharedDevicePeople,
  sharedDeviceSwitchIn,
  sharedDeviceSwitchOut,
  setSharedDevicePinByPhone,
  signInSharedDeviceSession,
  signOutSharedDeviceSession,
  useSharedDeviceClaim,
  type StoredSharedDeviceCredentials,
} from '../../services/opsSharedDevicesService'
import { clearCountDrafts } from '../../services/helper/helperCountDraftStore'
import { clearHelperOpsPhoneItems } from '../../services/helper/helperStore'
import { pinHelperWorkspaceForSharedDevice } from '../../services/helper/helperStore'
import {
  sendBookingPhoneOtp,
  signOutBookingAuth,
  type ConfirmationResult,
} from '../../services/bookingAuthApp'
import { HelperDashboardLayout } from '../Helper/HelperDashboardLayout'
import { mapSharedDeviceError } from './sharedDeviceErrorMapper'
import {
  deleteSharedDeviceOfflineStore,
  saveCachedPeople,
  storeOfflinePinVerifier,
  listOfflineEligible,
  verifyOfflinePin,
  getOfflineLockoutUntil,
  startOfflineSession,
  endOfflineSession,
} from '../../services/sharedDeviceOfflineStore'
import {
  setSharedDeviceWorkRouterContext,
  clearSharedDeviceWorkRouterContext,
} from '../../services/sharedDeviceWorkRouter'
import {
  openPersonCache,
  closePersonCache,
  deleteExpiredPersonSnapshots,
  refreshSharedDeviceStockCache,
} from '../../services/sharedDevicePersonCache'
import { flushSharedDeviceOutbox } from '../../services/sharedDeviceOutbox'
import './sharedDevice.css'

function generateOpId(prefix: string): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}-${crypto.randomUUID()}`
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

interface ActiveSharedSession {
  sessionId: string
  uid: string
  idleLockMinutes: number
}

export function SharedDeviceShell(): React.ReactElement {
  const { t } = useNls()
  const { currentUser } = useAuth()
  const hasSharedDeviceClaim = useSharedDeviceClaim()

  // Credential state
  const [creds, setCreds] = useState<StoredSharedDeviceCredentials | null>(() =>
    getStoredSharedDeviceCredentials(),
  )

  // Enrol form states
  const [enrolCode, setEnrolCode] = useState('')
  const [enrolling, setEnrolling] = useState(false)
  const [enrolConfirmOpen, setEnrolConfirmOpen] = useState(false)
  const [enrolError, setEnrolError] = useState<string | null>(null)

  // People list states
  const [people, setPeople] = useState<SharedDevicePersonSummary[]>([])
  const [loadingPeople, setLoadingPeople] = useState(false)
  const [peopleError, setPeopleError] = useState<string | null>(null)
  const [isOnline, setIsOnline] = useState(() => (typeof navigator !== 'undefined' ? navigator.onLine : true))

  // Active session state (floor work mode)
  const [currentSession, setCurrentSession] = useState<ActiveSharedSession | null>(null)
  const [sessionKey, setSessionKey] = useState(1)

  // PIN modal states
  const [selectedPerson, setSelectedPerson] = useState<SharedDevicePersonSummary | null>(null)
  const [pinInput, setPinInput] = useState('')
  const [pinError, setPinError] = useState<string | null>(null)
  const [switchingIn, setSwitchingIn] = useState(false)
  const [switchInOpId, setSwitchInOpId] = useState(() => generateOpId('switch-in'))

  // Phone OTP flow states ("No PIN yet")
  const [phoneOtpOpen, setPhoneOtpOpen] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [newPin, setNewPin] = useState('')
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null)
  const [phoneOtpStep, setPhoneOtpStep] = useState<'PHONE' | 'OTP'>('PHONE')
  const [phoneOtpError, setPhoneOtpError] = useState<string | null>(null)
  const [phoneOtpBusy, setPhoneOtpBusy] = useState(false)

  // Idle lock timer ref
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Handle revoked device error
  const handleDeviceRevoked = useCallback(() => {
    if (creds?.deviceId) {
      void deleteSharedDeviceOfflineStore(creds.deviceId).catch((err) => {
        console.warn('[SharedDevice] Failed to delete offline store on revoke:', err)
      })
    }
    clearSharedDeviceWorkRouterContext()
    closePersonCache()
    if (creds?.deviceId && currentSession?.sessionId.startsWith('offline-')) {
      void endOfflineSession(creds.deviceId, currentSession.sessionId, 'REVOKED').catch(() => {})
    }
    pinHelperWorkspaceForSharedDevice(null)
    clearStoredSharedDeviceCredentials()
    setCreds(null)
    setCurrentSession(null)
    setPeopleError(t('NLSOpsSharedDeviceRevoked'))
  }, [creds, currentSession, t])

  // Delete expired snapshots on startup
  useEffect(() => {
    if (creds?.deviceId) {
      void deleteExpiredPersonSnapshots(creds.deviceId).catch((err) => {
        console.warn('[SharedDevice] Failed to delete expired person snapshots:', err)
      })
    }
  }, [creds?.deviceId])

  // Fetch / refresh people list
  const refreshPeople = useCallback(async () => {
    if (!creds?.deviceId) return
    setLoadingPeople(true)
    setPeopleError(null)

    if (!isOnline) {
      try {
        const offlineList = await listOfflineEligible(creds.deviceId)
        setPeople(
          offlineList.map((p) => ({
            uid: p.uid,
            displayName: p.name,
            initials: p.initials,
            hasPin: p.offlineEligible,
          })),
        )
      } catch (err) {
        console.warn('[SharedDevice] Failed to list offline eligible people:', err)
      } finally {
        setLoadingPeople(false)
      }
      return
    }

    if (!creds.deviceSecret) {
      setLoadingPeople(false)
      return
    }

    try {
      const res = await listSharedDevicePeople({
        deviceId: creds.deviceId,
        deviceSecret: creds.deviceSecret,
      })
      const peopleList = res.people || []
      setPeople(peopleList)
      void saveCachedPeople(creds.deviceId, peopleList).catch((err) => {
        console.warn('[SharedDevice] Failed to cache people for offline:', err)
      })
    } catch (err) {
      const mapped = mapSharedDeviceError(err)
      if (mapped.state === 'revoked') {
        handleDeviceRevoked()
        return
      }
      setPeopleError(t(mapped.nlsKey))
    } finally {
      setLoadingPeople(false)
    }
  }, [creds, handleDeviceRevoked, isOnline, t])

  // Online / offline tracking & outbox flush on online
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const handleOnline = () => {
      setIsOnline(true)
      if (creds?.deviceId && creds?.deviceSecret) {
        void flushSharedDeviceOutbox(creds)
          .then((res) => {
            if (res.revokedStopped) {
              handleDeviceRevoked()
            }
          })
          .catch((err) => {
            console.warn('[SharedDevice] Outbox flush on online failed:', err)
          })
      }
    }
    const handleOffline = () => {
      setIsOnline(false)
      refreshPeople()
    }
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [creds, handleDeviceRevoked, refreshPeople])

  // Load people list on mount or when creds change
  useEffect(() => {
    if (creds && !currentSession) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- triggers async people load when enrolled
      refreshPeople()
    }
  }, [creds, currentSession, refreshPeople])

  // Refresh on focus and every 5 minutes
  useEffect(() => {
    if (!creds || currentSession) return undefined
    const handleFocus = () => {
      refreshPeople()
    }
    window.addEventListener('focus', handleFocus)
    const interval = setInterval(refreshPeople, 5 * 60 * 1000)
    return () => {
      window.removeEventListener('focus', handleFocus)
      clearInterval(interval)
    }
  }, [creds, currentSession, refreshPeople])

  // Switch Out helper
  const performSwitchOut = useCallback(
    async (reason: 'SWITCH' | 'IDLE') => {
      if (!creds || !currentSession) return
      const { sessionId, uid } = currentSession

      // Discard count drafts and ops phone items for clean start
      clearCountDrafts(uid, creds.workspaceId)
      clearHelperOpsPhoneItems(creds.workspaceId)

      // Clear the pin and persisted active workspace for the person who left
      pinHelperWorkspaceForSharedDevice(null)

      if (sessionId.startsWith('offline-')) {
        await endOfflineSession(creds.deviceId, sessionId, reason)
        clearSharedDeviceWorkRouterContext()
        closePersonCache()
      } else {
        try {
          await sharedDeviceSwitchOut({
            deviceId: creds.deviceId,
            deviceSecret: creds.deviceSecret,
            sessionId,
            reason,
          })
        } catch {
          // Continue to sign out even if network call fails
        }
        await signOutSharedDeviceSession()
        closePersonCache()
      }

      setCurrentSession(null)
      setSelectedPerson(null)
      setPinInput('')
      setSessionKey((prev) => prev + 1)
    },
    [creds, currentSession],
  )

  // Listen for manual switch-out event from HelperDashboardLayout
  useEffect(() => {
    const handleSwitchOutEvent = (e: Event) => {
      const reason = (e as CustomEvent<{ reason?: 'SWITCH' | 'IDLE' }>).detail?.reason ?? 'SWITCH'
      void performSwitchOut(reason)
    }
    window.addEventListener('shared-device:switch-out', handleSwitchOutEvent)
    return () => {
      window.removeEventListener('shared-device:switch-out', handleSwitchOutEvent)
    }
  }, [performSwitchOut])

  // Idle lock watchdog
  useEffect(() => {
    if (!currentSession || !creds) return undefined

    const idleMinutes = currentSession.idleLockMinutes || creds.idleLockMinutes || 2
    const idleMs = idleMinutes * 60 * 1000

    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      idleTimerRef.current = setTimeout(() => {
        void performSwitchOut('IDLE')
      }, idleMs)
    }

    resetIdleTimer()

    const events = ['touchstart', 'pointerdown', 'keydown', 'scroll', 'mousemove']
    const handleUserActivity = () => {
      resetIdleTimer()
    }

    for (const evt of events) {
      window.addEventListener(evt, handleUserActivity, { passive: true })
    }

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      for (const evt of events) {
        window.removeEventListener(evt, handleUserActivity)
      }
    }
  }, [currentSession, creds, performSwitchOut])

  // Handle Enrol submission
  const doEnrol = async () => {
    if (!enrolCode.trim()) return
    setEnrolling(true)
    setEnrolError(null)
    try {
      const res = await enrolSharedDevice({ code: enrolCode.trim() })
      setStoredSharedDeviceCredentials(res)
      setCreds(res)
      setEnrolCode('')
    } catch (err) {
      const mapped = mapSharedDeviceError(err)
      setEnrolError(t(mapped.nlsKey))
    } finally {
      setEnrolling(false)
    }
  }

  const handleEnrolClick = async () => {
    // If a normal user is signed in, confirm before sign-out
    if (currentUser && !hasSharedDeviceClaim) {
      setEnrolConfirmOpen(true)
      return
    }
    await doEnrol()
  }

  const handleConfirmEnrolSignOut = async () => {
    setEnrolConfirmOpen(false)
    await signOutSharedDeviceSession()
    await doEnrol()
  }

  // Handle tap person
  const handleSelectPerson = (person: SharedDevicePersonSummary) => {
    if (!isOnline && !person.hasPin) {
      setPeopleError(t('NLSOpsSharedDeviceOfflineConnect'))
      return
    }
    setSelectedPerson(person)
    setPinInput('')
    setPinError(null)

    if (isOnline && !person.hasPin) {
      // Start phone OTP flow
      setPhoneOtpOpen(true)
      setPhoneOtpStep('PHONE')
      setPhoneNumber('')
      setOtpCode('')
      setNewPin('')
      setPhoneOtpError(null)
    }
  }

  // Handle PIN submission
  const handlePinSubmit = async (overridePin?: string) => {
    const pinToUse = typeof overridePin === 'string' ? overridePin : pinInput
    if (!creds || !selectedPerson || pinToUse.length !== 4) return
    setSwitchingIn(true)
    setPinError(null)

    if (!isOnline) {
      try {
        const verifyRes = await verifyOfflinePin(creds.deviceId, selectedPerson.uid, pinToUse)
        if (verifyRes === 'WRONG') {
          setPinError(t('NLSOpsSharedDeviceOfflinePinWrong'))
          setPinInput('')
          return
        }
        if (verifyRes === 'EXPIRED') {
          setPinError(t('NLSOpsSharedDeviceOfflinePinExpired'))
          setPinInput('')
          return
        }
        if (verifyRes === 'NO_VERIFIER') {
          setPinError(t('NLSOpsSharedDeviceOfflinePinNoVerifier'))
          setPinInput('')
          return
        }
        if (verifyRes === 'LOCKED') {
          const lockedUntil = await getOfflineLockoutUntil(creds.deviceId, selectedPerson.uid)
          const timeStr = lockedUntil
            ? new Date(lockedUntil).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : ''
          setPinError(t('NLSOpsSharedDeviceOfflinePinLocked', { time: timeStr }))
          setPinInput('')
          return
        }
        if (verifyRes === 'OK') {
          const sessionId = await startOfflineSession(creds.deviceId, selectedPerson.uid)
          setSharedDeviceWorkRouterContext({
            deviceId: creds.deviceId,
            personUid: selectedPerson.uid,
            offlineSessionId: sessionId,
          })
          await openPersonCache(creds.deviceId, selectedPerson.uid, pinToUse, Date.now(), false)
          pinHelperWorkspaceForSharedDevice(creds.workspaceId)
          setCurrentSession({
            sessionId,
            uid: selectedPerson.uid,
            idleLockMinutes: creds.idleLockMinutes || 2,
          })
          setSelectedPerson(null)
          setPinInput('')
          return
        }
      } catch (err) {
        console.warn('[SharedDevice] Offline switch-in failed:', err)
        setPinError(t('NLSOpsSharedDeviceOfflinePinWrong'))
      } finally {
        setSwitchingIn(false)
      }
      return
    }

    try {
      const res = await sharedDeviceSwitchIn({
        deviceId: creds.deviceId,
        deviceSecret: creds.deviceSecret,
        uid: selectedPerson.uid,
        pin: pinToUse,
        opId: switchInOpId,
      })

      // Store offline verifier (non-blocking)
      void storeOfflinePinVerifier(
        creds.deviceId,
        selectedPerson.uid,
        pinToUse,
        res.lastOnlineSwitchInAt,
      ).catch((err) => {
        console.warn('[SharedDevice] Failed to store offline PIN verifier:', err)
      })

      // Open person cache with derived key for this session
      void openPersonCache(
        creds.deviceId,
        selectedPerson.uid,
        pinToUse,
        Date.now(),
        true,
        res.lastOnlineSwitchInAt ?? Date.now(),
      )
        .then(() => {
          void refreshSharedDeviceStockCache(creds.workspaceId).catch((err) => {
            console.warn('[SharedDevice] Failed to refresh stock cache on switch-in:', err)
          })
        })
        .catch((err) => {
          console.warn('[SharedDevice] Failed to open person cache on online switch-in:', err)
        })

      // Trigger outbox flush (non-blocking)
      void flushSharedDeviceOutbox(creds)
        .then((flushRes) => {
          if (flushRes.revokedStopped) {
            handleDeviceRevoked()
          }
        })
        .catch((err) => {
          console.warn('[SharedDevice] Outbox flush after switch-in failed:', err)
        })

      // Sign in auth session with customToken
      await signInSharedDeviceSession(res.customToken)

      pinHelperWorkspaceForSharedDevice(creds.workspaceId)
      setCurrentSession({
        sessionId: res.sessionId,
        uid: selectedPerson.uid,
        idleLockMinutes: creds.idleLockMinutes || 2,
      })
      setSwitchInOpId(generateOpId('switch-in'))
      setSelectedPerson(null)
      setPinInput('')
    } catch (err) {
      const mapped = mapSharedDeviceError(err)
      if (mapped.state === 'revoked') {
        handleDeviceRevoked()
        return
      }

      if (mapped.state === 'noPin') {
        setPhoneOtpOpen(true)
        setPhoneOtpStep('PHONE')
        setPhoneNumber('')
        setOtpCode('')
        setNewPin('')
        setPhoneOtpError(null)
        setPinError(null)
        return
      }

      if (mapped.state === 'locked') {
        setPinError(t(mapped.nlsKey, { minutes: mapped.minutes }))
      } else if (mapped.state === 'wrongPin') {
        setPinError(t(mapped.nlsKey, { count: mapped.triesLeft }))
      } else {
        setPinError(t(mapped.nlsKey))
      }
    } finally {
      setSwitchingIn(false)
    }
  }

  // Send Phone OTP
  const handleSendPhoneOtp = async () => {
    if (!phoneNumber.trim()) return
    setPhoneOtpBusy(true)
    setPhoneOtpError(null)
    try {
      const conf = await sendBookingPhoneOtp(phoneNumber.trim())
      setConfirmationResult(conf)
      setPhoneOtpStep('OTP')
    } catch (err) {
      const mapped = mapSharedDeviceError(err)
      if (mapped.state === 'revoked') {
        handleDeviceRevoked()
        return
      }
      setPhoneOtpError(t(mapped.nlsKey))
    } finally {
      setPhoneOtpBusy(false)
    }
  }

  // Confirm Phone OTP and set PIN
  const handleConfirmPhoneOtp = async () => {
    if (!creds || !confirmationResult || !otpCode.trim() || newPin.length !== 4) return
    const val = validateDevicePin(newPin)
    if (!val.valid) {
      setPhoneOtpError(val.error || t('NLSOpsSharedDevicePinWeak'))
      return
    }

    setPhoneOtpBusy(true)
    setPhoneOtpError(null)
    try {
      const userCred = await confirmationResult.confirm(otpCode.trim())
      const idToken = await userCred.user.getIdToken()
      const opId = generateOpId('phone-pin')

      await setSharedDevicePinByPhone({
        deviceId: creds.deviceId,
        deviceSecret: creds.deviceSecret,
        phoneIdToken: idToken,
        pin: newPin,
        opId,
      })

      await signOutBookingAuth()
      setPhoneOtpOpen(false)

      // Refresh list to update hasPin marker
      await refreshPeople()

      // Automatically attempt switch-in with new PIN
      if (selectedPerson) {
        const switchRes = await sharedDeviceSwitchIn({
          deviceId: creds.deviceId,
          deviceSecret: creds.deviceSecret,
          uid: selectedPerson.uid,
          pin: newPin,
          opId: generateOpId('switch-in'),
        })

        // Store offline verifier (non-blocking)
        void storeOfflinePinVerifier(
          creds.deviceId,
          selectedPerson.uid,
          newPin,
          switchRes.lastOnlineSwitchInAt,
        ).catch((err) => {
          console.warn('[SharedDevice] Failed to store offline PIN verifier:', err)
        })

        // Trigger outbox flush (non-blocking)
        void flushSharedDeviceOutbox(creds)
          .then((flushRes) => {
            if (flushRes.revokedStopped) {
              handleDeviceRevoked()
            }
          })
          .catch((err) => {
            console.warn('[SharedDevice] Outbox flush after switch-in failed:', err)
          })

        await signInSharedDeviceSession(switchRes.customToken)
        pinHelperWorkspaceForSharedDevice(creds.workspaceId)
        setCurrentSession({
          sessionId: switchRes.sessionId,
          uid: selectedPerson.uid,
          idleLockMinutes: creds.idleLockMinutes || 2,
        })
        setSelectedPerson(null)
      }
    } catch (err) {
      const mapped = mapSharedDeviceError(err)
      if (mapped.state === 'revoked') {
        handleDeviceRevoked()
        return
      }
      setPhoneOtpError(t(mapped.nlsKey))
    } finally {
      setPhoneOtpBusy(false)
    }
  }

  // 1. FLOOR WORK MODE: Render Helper Dashboard Layout for active session
  if (creds && currentSession) {
    return (
      <div className="sharedDeviceShell" data-testid="shared-device-floor-mode">
        <HelperDashboardLayout
          key={sessionKey}
          sharedDeviceMode={true}
          deviceWorkspaceId={creds.workspaceId}
        />
      </div>
    )
  }

  // 2. NOT ENROLLED VIEW
  if (!creds) {
    return (
      <div className="sharedDeviceShell" data-testid="shared-device-enrol-view">
        <header className="sharedDeviceHeader">
          <div className="sharedDeviceHeaderTitles">
            <h1 className="sharedDeviceHeaderLabel">
              {t('NLSOpsSharedDeviceShellTitle')}
            </h1>
          </div>
        </header>

        <main className="sharedDeviceMain">
          {peopleError && (
            <div className="sharedDeviceErrorBanner" data-testid="shared-device-revoked-banner">
              {peopleError}
            </div>
          )}

          <section className="sharedDeviceCard">
            <h2 className="sharedDeviceHeaderLabel">
              {t('NLSOpsSharedDeviceNotEnrolled')}
            </h2>
            <p className="sharedDeviceHeaderPlace">
              {t('NLSOpsSharedDeviceEnterEnrolCode')}
            </p>

            {enrolError && (
              <div className="sharedDeviceErrorBanner" data-testid="enrol-error-banner">
                {enrolError}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <TextInput
                value={enrolCode}
                onChange={(val) => setEnrolCode(val.trim())}
                placeholder="6-digit code"
                data-testid="enrol-code-input"
              />
              <Button
                variant="primary"
                onClick={handleEnrolClick}
                disabled={enrolling || !enrolCode.trim()}
                loading={enrolling}
                data-testid="enrol-submit-btn"
              >
                {t('NLSOpsSharedDeviceEnrolSubmit')}
              </Button>
            </div>
          </section>
        </main>

        {enrolConfirmOpen && (
          <ConfirmActionModal
            open={enrolConfirmOpen}
            onClose={() => setEnrolConfirmOpen(false)}
            onConfirm={handleConfirmEnrolSignOut}
            title={t('NLSOpsSharedDeviceShellTitle')}
            body={t('NLSOpsSharedDeviceSignOutExistingUser')}
            confirmLabel={t('NLSOpsSharedDeviceEnrolSubmit')}
          />
        )}
      </div>
    )
  }

  // 3. ENROLLED VIEW: People List
  return (
    <div className="sharedDeviceShell" data-testid="shared-device-people-view">
      <header className="sharedDeviceHeader">
        <div className="sharedDeviceHeaderTitles">
          <h1 className="sharedDeviceHeaderLabel" data-testid="shared-device-header-label">
            {creds.label}
          </h1>
          <p className="sharedDeviceHeaderPlace" data-testid="shared-device-header-place">
            {creds.placeId}
          </p>
        </div>
      </header>

      <main className="sharedDeviceMain">
        {!isOnline && (
          <div className="sharedDeviceOfflineBanner" data-testid="shared-device-offline-banner">
            {t('NLSOpsSharedDeviceOfflineConnect')}
          </div>
        )}

        {peopleError && (
          <div className="sharedDeviceErrorBanner" data-testid="shared-device-people-error">
            {peopleError}
          </div>
        )}

        <section className="sharedDeviceCard">
          <h2 className="sharedDeviceHeaderLabel">
            {t('NLSOpsSharedDevicePeopleTitle')}
          </h2>

          {loadingPeople && people.length === 0 ? (
            <p className="sharedDeviceHeaderPlace">{t('NLSOpsSharedDevicePeopleTitle')}</p>
          ) : (
            <div className="sharedDevicePeopleGrid" data-testid="shared-device-people-grid">
              {people.map((p) => (
                <div
                  key={p.uid}
                  className="sharedDevicePersonTile"
                  onClick={() => handleSelectPerson(p)}
                  data-testid={`person-tile-${p.uid}`}
                >
                  <div className="sharedDevicePersonAvatar">{p.initials}</div>
                  <span className="sharedDevicePersonName">{p.displayName}</span>
                  <span className="sharedDevicePersonBadge">
                    {p.hasPin
                      ? t('NLSOpsSharedDevicePinSetBadge')
                      : t('NLSOpsSharedDeviceNoPinBadge')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* PIN Modal for user switch-in */}
      {selectedPerson && selectedPerson.hasPin && !phoneOtpOpen && (
        <AppDialog
          open={Boolean(selectedPerson && selectedPerson.hasPin)}
          onDismiss={() => {
            setSelectedPerson(null)
            setPinInput('')
            setPinError(null)
          }}
          title={selectedPerson.displayName}
          data-testid="shared-device-pin-modal"
          actions={[
            {
              label: t('NLSCommonActionCancel'),
              variant: 'secondary',
              onClick: () => {
                setSelectedPerson(null)
                setPinInput('')
                setPinError(null)
              },
            },
            {
              label: t('NLSOpsSharedDeviceShellTitle'),
              variant: 'primary',
              onClick: () => void handlePinSubmit(),
              disabled: switchingIn || pinInput.length !== 4,
              loading: switchingIn,
              testId: 'pin-submit-btn',
            },
          ]}
        >
          <div className="sharedDevicePinModalContent">
            <p className="sharedDeviceHeaderPlace">
              {t('NLSOpsSharedDeviceEnterPinPrompt')}
            </p>

            {pinError && (
              <div className="sharedDeviceErrorBanner" data-testid="pin-error-banner">
                <span data-testid="shared-device-pin-error">{pinError}</span>
              </div>
            )}

            <TextInput
              value={pinInput}
              onChange={(val) => {
                const digits = val.replace(/\D/g, '').slice(0, 4)
                setPinInput(digits)
                if (pinError) setPinError(null)
                if (digits.length === 4) {
                  void handlePinSubmit(digits)
                }
              }}
              placeholder="••••"
              data-testid="shared-device-pin-input"
            />

            <div className="sharedDeviceKeypad" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginTop: '12px' }}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((d) => (
                <button
                  key={d}
                  type="button"
                  data-testid={`pin-digit-${d}`}
                  className="sharedDeviceKeypadDigit"
                  onClick={() => {
                    if (switchingIn) return
                    const next = (pinInput + d).slice(0, 4)
                    setPinInput(next)
                    if (pinError) setPinError(null)
                    if (next.length === 4) {
                      void handlePinSubmit(next)
                    }
                  }}
                  style={{ padding: '12px', fontSize: '18px', borderRadius: '8px' }}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
        </AppDialog>
      )}

      {/* Phone OTP Modal for "No PIN yet" */}
      {phoneOtpOpen && (
        <AppDialog
          open={phoneOtpOpen}
          onDismiss={() => {
            setPhoneOtpOpen(false)
            setSelectedPerson(null)
          }}
          title={t('NLSOpsSharedDeviceNoPinYet')}
          data-testid="shared-device-otp-modal"
          actions={[
            {
              label: t('NLSCommonActionCancel'),
              variant: 'secondary',
              onClick: () => {
                setPhoneOtpOpen(false)
                setSelectedPerson(null)
              },
              disabled: phoneOtpBusy,
            },
            phoneOtpStep === 'PHONE'
              ? {
                  label: t('NLSOpsSharedDeviceSendOtp'),
                  variant: 'primary',
                  onClick: handleSendPhoneOtp,
                  disabled: phoneOtpBusy || !phoneNumber.trim(),
                  loading: phoneOtpBusy,
                  testId: 'send-otp-btn',
                }
              : {
                  label: t('NLSOpsSharedDeviceConfirmOtp'),
                  variant: 'primary',
                  onClick: handleConfirmPhoneOtp,
                  disabled: phoneOtpBusy || !otpCode.trim() || newPin.length !== 4,
                  loading: phoneOtpBusy,
                  testId: 'confirm-otp-btn',
                },
          ]}
        >
          <div className="sharedDevicePinModalContent">
            {phoneOtpError && (
              <div className="sharedDeviceErrorBanner" data-testid="otp-error-banner">
                {phoneOtpError}
              </div>
            )}

            {phoneOtpStep === 'PHONE' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span className="sharedDeviceHeaderPlace">
                  {t('NLSOpsSharedDevicePhonePrompt')}
                </span>
                <TextInput
                  value={phoneNumber}
                  onChange={(val) => setPhoneNumber(val)}
                  placeholder="+1234567890"
                  data-testid="otp-phone-input"
                />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span className="sharedDeviceHeaderPlace">
                    {t('NLSOpsSharedDeviceEnterOtp')}
                  </span>
                  <TextInput
                    value={otpCode}
                    onChange={(val) => setOtpCode(val.trim())}
                    placeholder="123456"
                    data-testid="otp-code-input"
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span className="sharedDeviceHeaderPlace">
                    {t('NLSOpsSharedDeviceEnterPin')}
                  </span>
                  <TextInput
                    value={newPin}
                    onChange={(val) => setNewPin(val.replace(/\D/g, '').slice(0, 4))}
                    placeholder="••••"
                    data-testid="otp-new-pin-input"
                  />
                </div>
              </div>
            )}
          </div>
        </AppDialog>
      )}
    </div>
  )
}
